import { AnalyticsService } from '@alison/analytics/client';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { CreativePreview } from '../../CreativePreview.js';
import type { DetailCreative } from '../../data/api.js';
import type { CreativeDetailScope } from '../data/api.js';
import { useSingleCreativeScore } from '../data/api.js';
import { ScoreGauge } from '../shared/ScoreGauge.js';
import { weakFactorOf, weakFactorSentence } from './weakFactor.js';

const SCORE_GAUGE_SIZE = 52;
const PREVIEW_MAX_SIZE = 280;

interface CreativeSummaryProps {
  creative: DetailCreative;
  scope: CreativeDetailScope;
  leadingKpiLabel: string;
  onSeeBreakdown: () => void;
}

export function CreativeSummary({
  creative,
  scope,
  leadingKpiLabel,
  onSeeBreakdown,
}: CreativeSummaryProps): ReactNode {
  const scoreQuery = useSingleCreativeScore({ ...scope, creativeId: creative.creativeId }, true);
  const creativeScore = scoreQuery.data;
  const factor =
    creativeScore === undefined ? null : weakFactorOf(creativeScore.components, leadingKpiLabel);

  return (
    <Stack sx={{ gap: 3, width: PREVIEW_MAX_SIZE, flexShrink: 0 }}>
      <CreativePreview
        name={creative.creativeId}
        creativeType={creative.creativeType}
        format={creative.format}
        duration={creative.duration}
        thumbnailUrl={creative.thumbnailUrl}
        previewUrl={creative.previewUrl}
        maxSize={PREVIEW_MAX_SIZE}
        caption={false}
      />

      {scoreQuery.isPending && <Skeleton variant="rounded" height={SCORE_GAUGE_SIZE} />}
      {creativeScore !== undefined && creativeScore.score !== null && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
          <ScoreGauge
            score={creativeScore.score}
            previous={creativeScore.previous}
            size={SCORE_GAUGE_SIZE}
          />
          <Stack sx={{ gap: 0.5, minWidth: 0 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Creative Score</Typography>
            {factor !== null && (
              <Typography sx={{ fontSize: 11, color: 'text.secondary', lineHeight: 1.4 }}>
                {weakFactorSentence(factor)}
              </Typography>
            )}
            <Box
              component="button"
              type="button"
              onClick={() => {
                AnalyticsService.track('Creative Detail - See Breakdown');
                onSeeBreakdown();
              }}
              sx={{
                alignSelf: 'flex-start',
                color: 'primary.main',
                cursor: 'pointer',
                border: 'none',
                background: 'none',
                fontFamily: 'inherit',
                fontSize: 12,
                fontWeight: 600,
                p: 0,
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              See breakdown
            </Box>
          </Stack>
        </Box>
      )}
    </Stack>
  );
}
